// WDC Global AI Worker System
// Autonomous workers that generate revenue through profit loops

import { 
  AIWorker, ProfitLoop, ProductId 
} from '../types';

export interface WorkerExecutionResult {
  success: boolean;
  output: any;
  costUsd: number;
  revenueUsd: number;
  durationMs: number;
  error?: string;
  metadata: Record<string, any>;
}

export class AIWorkerEngine {
  private workers: Map<string, AIWorker> = new Map();
  private profitLoops: Map<string, ProfitLoop> = new Map();
  private runningIntervals: Map<string, NodeJS.Timeout> = new Map();

  // Model costs per 1M tokens (input/output)
  private readonly MODEL_COSTS: Record<string, { input: number; output: number }> = {
    'nvidia/nemotron-3-ultra': { input: 0.50, output: 2.50 },
    'nvidia/nemotron-3-ultra-550b': { input: 0.50, output: 2.50 },
    'openai/gpt-4o': { input: 5.00, output: 15.00 },
    'openai/gpt-4o-mini': { input: 0.15, output: 0.60 },
    'anthropic/claude-3.5-sonnet': { input: 3.00, output: 15.00 },
    'anthropic/claude-3.5-haiku': { input: 0.25, output: 1.25 },
    'xai/grok-2': { input: 2.00, output: 10.00 },
    'mistral/large': { input: 2.00, output: 6.00 },
  };

  registerWorker(worker: AIWorker): void {
    this.workers.set(worker.id, worker);
    if (worker.schedule.enabled) {
      this.scheduleWorker(worker);
    }
  }

  unregisterWorker(workerId: string): void {
    const worker = this.workers.get(workerId);
    if (worker) {
      this.stopWorker(workerId);
      this.workers.delete(workerId);
    }
  }

  getWorker(workerId: string): AIWorker | undefined {
    return this.workers.get(workerId);
  }

  getAllWorkers(): AIWorker[] {
    return Array.from(this.workers.values());
  }

  getWorkersByProduct(productId: ProductId): AIWorker[] {
    return Array.from(this.workers.values()).filter(w => w.productId === productId);
  }

  async executeWorker(workerId: string): Promise<WorkerExecutionResult> {
    const worker = this.workers.get(workerId);
    if (!worker) {
      return { success: false, output: null, costUsd: 0, revenueUsd: 0, durationMs: 0, error: 'Worker not found', metadata: {} };
    }

    if (worker.status === 'running') {
      return { success: false, output: null, costUsd: 0, revenueUsd: 0, durationMs: 0, error: 'Worker already running', metadata: {} };
    }

    const startTime = Date.now();
    worker.status = 'running';
    this.workers.set(workerId, worker);

    try {
      const result = await this.runWorkerTask(worker);
      const durationMs = Date.now() - startTime;
      
      // Update worker metrics
      worker.metrics.runsTotal++;
      if (result.success) {
        worker.metrics.runsSuccessful++;
        worker.metrics.totalRevenueUsd += result.revenueUsd;
      } else {
        worker.metrics.runsFailed++;
      }
      worker.metrics.totalCostUsd += result.costUsd;
      worker.metrics.avgDurationMs = (worker.metrics.avgDurationMs * (worker.metrics.runsTotal - 1) + durationMs) / worker.metrics.runsTotal;
      worker.metrics.netProfitUsd = worker.metrics.totalRevenueUsd - worker.metrics.totalCostUsd;
      worker.updatedAt = new Date();
      
      this.workers.set(workerId, worker);

      // If worker has a profit loop, update it
      if (worker.profitLoop) {
        this.updateProfitLoopMetrics(worker.profitLoop.id, result);
      }

      return { ...result, durationMs };
    } catch (error) {
      worker.status = 'error';
      worker.metrics.runsTotal++;
      worker.metrics.runsFailed++;
      this.workers.set(workerId, worker);
      
      return { 
        success: false, 
        output: null, 
        costUsd: 0, 
        revenueUsd: 0, 
        durationMs: Date.now() - startTime, 
        error: error instanceof Error ? error.message : 'Unknown error',
        metadata: {}
      };
    } finally {
      worker.status = 'idle';
      this.workers.set(workerId, worker);
    }
  }

  private async runWorkerTask(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Simulate different worker types
    switch (worker.type) {
      case 'research':
        return this.runResearchWorker(worker);
      case 'content_generation':
        return this.runContentWorker(worker);
      case 'monitoring':
        return this.runMonitoringWorker(worker);
      case 'trading':
        return this.runTradingWorker(worker);
      case 'optimization':
        return this.runOptimizationWorker(worker);
      case 'compliance_check':
        return this.runComplianceWorker(worker);
      case 'permit_tracking':
        return this.runPermitWorker(worker);
      case 'regulatory_scan':
        return this.runRegulatoryWorker(worker);
      default:
        return { success: false, output: null, costUsd: 0, revenueUsd: 0, durationMs: 0, error: 'Unknown worker type', metadata: {} };
    }
  }

  private async runResearchWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Simulate market research that can be sold as data/API
    const tokensUsed = Math.floor(Math.random() * 50000) + 10000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.3);
    
    // Research can be monetized via data sales or API
    const revenueUsd = Math.random() * 50 + 10; // $10-60 per research report
    
    return {
      success: true,
      output: { reportId: `rpt_${Date.now()}`, topics: ['market_trends', 'competitor_analysis', 'opportunity_detection'], insights: 5 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runContentWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Generate content for SEO, affiliate sites, newsletters
    const tokensUsed = Math.floor(Math.random() * 30000) + 5000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.5);
    
    // Content monetized via affiliate, ad revenue, or direct sales
    const revenueUsd = Math.random() * 100 + 20; // $20-120 per content piece
    
    return {
      success: true,
      output: { contentId: `cnt_${Date.now()}`, type: 'article', words: 2000, seoScore: 85, affiliateLinks: 3 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runMonitoringWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Monitor markets, competitors, regulations
    const tokensUsed = Math.floor(Math.random() * 20000) + 5000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.2);
    
    // Monitoring feeds into alert products (RegWatch, ComplianceOS)
    const revenueUsd = Math.random() * 30 + 5; // $5-35 per alert cycle
    
    return {
      success: true,
      output: { alertsGenerated: Math.floor(Math.random() * 10) + 1, sourcesChecked: 50, changesDetected: 3 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runTradingWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Algorithmic trading / crypto arbitrage (high risk, high reward)
    const tokensUsed = Math.floor(Math.random() * 10000) + 2000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.3);
    
    // Trading can be profitable or loss-making
    const pnl = (Math.random() - 0.45) * 200; // Slight edge: 55% win rate, avg $200
    const revenueUsd = Math.max(0, pnl);
    
    return {
      success: pnl >= 0,
      output: { trades: Math.floor(Math.random() * 20) + 5, pnl, winRate: 0.55 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model, risk: 'high' }
    };
  }

  private async runOptimizationWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // Optimize ad spend, pricing, resource allocation
    const tokensUsed = Math.floor(Math.random() * 15000) + 3000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.4);
    
    // Optimization saves money or increases revenue
    const savings = Math.random() * 500 + 50; // $50-550 savings
    const revenueUsd = savings * 0.3; // Take 30% of savings as revenue
    
    return {
      success: true,
      output: { optimizations: 3, estimatedSavings: savings, recommendations: ['reduce_ad_spend', 'increase_bid', 'pause_underperforming'] },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runComplianceWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // ComplianceOS: automated compliance checks
    const tokensUsed = Math.floor(Math.random() * 25000) + 5000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.3);
    
    // Compliance checks are billable workflows
    const revenueUsd = Math.random() * 100 + 50; // $50-150 per assessment
    
    return {
      success: true,
      output: { frameworksChecked: ['SOC2', 'ISO27001'], controlsVerified: 45, findings: 2, score: 92 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runPermitWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // PermitFlow: track permits, generate docs
    const tokensUsed = Math.floor(Math.random() * 20000) + 5000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.4);
    
    // Permit workflows are billable
    const revenueUsd = Math.random() * 200 + 100; // $100-300 per permit
    
    return {
      success: true,
      output: { permitsTracked: 12, applicationsSubmitted: 3, approvals: 2, deadlinesMet: 11 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private async runRegulatoryWorker(worker: AIWorker): Promise<WorkerExecutionResult> {
    // RegWatch: scan regulations, assess impact
    const tokensUsed = Math.floor(Math.random() * 30000) + 10000;
    const costUsd = this.calculateTokenCost(worker.config.model, tokensUsed, tokensUsed * 0.3);
    
    // Regulatory intelligence is billable per resolution
    const revenueUsd = Math.random() * 80 + 20; // $20-100 per scan cycle
    
    return {
      success: true,
      output: { regulationsScanned: 150, newAlerts: 5, impactAssessments: 3, jurisdictions: 12 },
      costUsd,
      revenueUsd,
      durationMs: 0,
      metadata: { tokensUsed, model: worker.config.model }
    };
  }

  private calculateTokenCost(model: string, inputTokens: number, outputTokens: number): number {
    const costs = this.MODEL_COSTS[model] || { input: 1.00, output: 3.00 };
    return (inputTokens / 1_000_000) * costs.input + (outputTokens / 1_000_000) * costs.output;
  }

  private scheduleWorker(worker: AIWorker): void {
    if (this.runningIntervals.has(worker.id)) {
      clearInterval(this.runningIntervals.get(worker.id)!);
    }

    // Parse cron expression (simplified - using minute interval for demo)
    const intervalMs = this.parseCronToMs(worker.schedule.expression);
    
    const interval = setInterval(() => {
      this.executeWorker(worker.id);
    }, intervalMs);

    this.runningIntervals.set(worker.id, interval);
    
    // Update next run
    worker.schedule.nextRun = new Date(Date.now() + intervalMs);
    this.workers.set(worker.id, worker);
  }

  private parseCronToMs(cron: string): number {
    // Simplified cron parsing - default to 15 minutes
    if (cron.includes('*/')) {
      const match = cron.match(/\*\/(\d+)/);
      if (match) return parseInt(match[1]) * 60 * 1000;
    }
    return 15 * 60 * 1000; // 15 minutes default
  }

  stopWorker(workerId: string): void {
    const interval = this.runningIntervals.get(workerId);
    if (interval) {
      clearInterval(interval);
      this.runningIntervals.delete(workerId);
    }
    const worker = this.workers.get(workerId);
    if (worker) {
      worker.schedule.enabled = false;
      this.workers.set(workerId, worker);
    }
  }

  // Profit Loop Management
  registerProfitLoop(loop: ProfitLoop): void {
    this.profitLoops.set(loop.id, loop);
  }

  getProfitLoop(loopId: string): ProfitLoop | undefined {
    return this.profitLoops.get(loopId);
  }

  getAllProfitLoops(): ProfitLoop[] {
    return Array.from(this.profitLoops.values());
  }

  getProfitLoopsByWorker(workerId: string): ProfitLoop[] {
    return Array.from(this.profitLoops.values()).filter(l => l.workerId === workerId);
  }

  private updateProfitLoopMetrics(loopId: string, result: WorkerExecutionResult): void {
    const loop = this.profitLoops.get(loopId);
    if (!loop) return;

    loop.metrics.totalRevenueUsd += result.revenueUsd;
    loop.metrics.totalCostUsd += result.costUsd;
    loop.metrics.netProfitUsd = loop.metrics.totalRevenueUsd - loop.metrics.totalCostUsd;
    loop.metrics.roiPercent = loop.metrics.totalCostUsd > 0 
      ? (loop.metrics.netProfitUsd / loop.metrics.totalCostUsd) * 100 
      : 0;
    loop.metrics.runsCount++;
    loop.metrics.avgRevenuePerRun = loop.metrics.totalRevenueUsd / loop.metrics.runsCount;
    loop.updatedAt = new Date();

    // Auto-pause if losing money beyond threshold
    if (loop.metrics.netProfitUsd < -loop.config.riskLimits.maxDailyLossUsd) {
      loop.status = 'paused';
    }

    this.profitLoops.set(loopId, loop);
  }

  // Create default workers for each product
  createDefaultWorkers(): AIWorker[] {
    const workers: AIWorker[] = [
      // Command Center workers
      {
        id: 'wc-research-001',
        name: 'Market Opportunity Scanner',
        type: 'research',
        status: 'idle',
        productId: 'command-center',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.3,
          maxTokens: 4000,
          tools: ['web_search', 'web_extract', 'data_analysis'],
          promptTemplate: 'Scan markets for high-margin SaaS opportunities...',
          constraints: { maxCostPerRun: 0.50, minOpportunityScore: 70 },
        },
        schedule: { expression: '*/30 * * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'wc-content-001',
        name: 'SEO Content Generator',
        type: 'content_generation',
        status: 'idle',
        productId: 'command-center',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.7,
          maxTokens: 3000,
          tools: ['web_search', 'keyword_research'],
          promptTemplate: 'Generate SEO-optimized content targeting high-value keywords...',
          constraints: { minWordCount: 1500, maxCostPerRun: 0.30 },
        },
        schedule: { expression: '0 */4 * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // AgentGuard workers
      {
        id: 'ag-monitor-001',
        name: 'Agent Threat Monitor',
        type: 'monitoring',
        status: 'idle',
        productId: 'agentguard',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.1,
          maxTokens: 2000,
          tools: ['log_analysis', 'anomaly_detection'],
          promptTemplate: 'Monitor agent fleet for anomalies, unauthorized access, policy violations...',
          constraints: { alertThreshold: 0.8, maxCostPerRun: 0.10 },
        },
        schedule: { expression: '*/5 * * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // ComplianceOS workers
      {
        id: 'co-compliance-001',
        name: 'Continuous Compliance Auditor',
        type: 'compliance_check',
        status: 'idle',
        productId: 'complianceos',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.2,
          maxTokens: 4000,
          tools: ['framework_mapping', 'evidence_collection'],
          promptTemplate: 'Run automated compliance assessment against selected frameworks...',
          constraints: { frameworks: ['SOC2', 'ISO27001', 'GDPR'], maxCostPerRun: 0.50 },
        },
        schedule: { expression: '0 2 * * *', timezone: 'UTC', enabled: true }, // Daily at 2 AM
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // PermitFlow workers
      {
        id: 'pf-permit-001',
        name: 'Permit Tracker & Auto-Filer',
        type: 'permit_tracking',
        status: 'idle',
        productId: 'permitflow',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.1,
          maxTokens: 3000,
          tools: ['jurisdiction_api', 'document_generation', 'deadline_tracking'],
          promptTemplate: 'Track permit statuses, auto-generate applications, alert on deadlines...',
          constraints: { jurisdictions: ['all'], maxCostPerRun: 0.30 },
        },
        schedule: { expression: '0 */6 * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // RegWatch workers
      {
        id: 'rw-scan-001',
        name: 'Regulatory Intelligence Scanner',
        type: 'regulatory_scan',
        status: 'idle',
        productId: 'regwatch',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.2,
          maxTokens: 4000,
          tools: ['regulatory_feed', 'impact_analysis', 'alert_generation'],
          promptTemplate: 'Scan regulatory sources for changes, assess impact on WDC products...',
          constraints: { sources: ['federal', 'state', 'industry', 'court'], maxCostPerRun: 0.25 },
        },
        schedule: { expression: '*/15 * * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      // Cross-product optimization worker
      {
        id: 'wc-optimize-001',
        name: 'Cross-Product Revenue Optimizer',
        type: 'optimization',
        status: 'idle',
        productId: 'command-center',
        config: {
          model: 'nvidia/nemotron-3-ultra',
          temperature: 0.3,
          maxTokens: 3000,
          tools: ['analytics', 'pricing_optimization', 'resource_allocation'],
          promptTemplate: 'Analyze cross-product metrics, optimize pricing, allocate resources for max profit...',
          constraints: { minMarginPercent: 40, maxCostPerRun: 0.40 },
        },
        schedule: { expression: '0 3 * * *', timezone: 'UTC', enabled: true },
        metrics: { runsTotal: 0, runsSuccessful: 0, runsFailed: 0, avgDurationMs: 0, totalCostUsd: 0, totalRevenueUsd: 0, netProfitUsd: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    workers.forEach(w => this.registerWorker(w));
    return workers;
  }

  // Create default profit loops
  createDefaultProfitLoops(): ProfitLoop[] {
    const loops: ProfitLoop[] = [
      {
        id: 'pl-content-seo-001',
        name: 'SEO Content Monetization Loop',
        description: 'Generate SEO content → rank → affiliate revenue + ad revenue',
        type: 'content_monetization',
        status: 'active',
        workerId: 'wc-content-001',
        config: {
          targetRevenueUsd: 5000,
          maxCostUsd: 500,
          minMarginPercent: 60,
          reinvestPercent: 30,
          channels: [
            { type: 'affiliate', config: { networks: ['amazon', 'shareasale', 'cj'] }, enabled: true },
            { type: 'stripe', config: { productId: 'command-center' }, enabled: true },
          ],
          riskLimits: { maxDailyLossUsd: 50, maxConcurrentWorkers: 3, killSwitchThreshold: -200 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'pl-data-api-001',
        name: 'Research Data API Sales',
        description: 'Market research → package as API → sell via subscription',
        type: 'data_sales',
        status: 'active',
        workerId: 'wc-research-001',
        config: {
          targetRevenueUsd: 10000,
          maxCostUsd: 1000,
          minMarginPercent: 70,
          reinvestPercent: 25,
          channels: [
            { type: 'api_marketplace', config: { platform: 'rapidapi', pricing: 'per_call' }, enabled: true },
            { type: 'stripe', config: { productId: 'command-center' }, enabled: true },
          ],
          riskLimits: { maxDailyLossUsd: 100, maxConcurrentWorkers: 2, killSwitchThreshold: -500 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'pl-compliance-workflow-001',
        name: 'Compliance Workflow Automation',
        description: 'Automated compliance checks → bill per workflow',
        type: 'api_usage',
        status: 'active',
        workerId: 'co-compliance-001',
        config: {
          targetRevenueUsd: 15000,
          maxCostUsd: 2000,
          minMarginPercent: 50,
          reinvestPercent: 20,
          channels: [
            { type: 'stripe', config: { productId: 'complianceos' }, enabled: true },
          ],
          riskLimits: { maxDailyLossUsd: 200, maxConcurrentWorkers: 5, killSwitchThreshold: -1000 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'pl-permit-automation-001',
        name: 'Permit Flow Automation Revenue',
        description: 'Permit tracking & filing → bill per project/permit',
        type: 'api_usage',
        status: 'active',
        workerId: 'pf-permit-001',
        config: {
          targetRevenueUsd: 20000,
          maxCostUsd: 3000,
          minMarginPercent: 55,
          reinvestPercent: 25,
          channels: [
            { type: 'stripe', config: { productId: 'permitflow' }, enabled: true },
          ],
          riskLimits: { maxDailyLossUsd: 300, maxConcurrentWorkers: 4, killSwitchThreshold: -1500 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'pl-regwatch-alerts-001',
        name: 'RegWatch Alert Subscriptions',
        description: 'Regulatory scanning → alert subscriptions → recurring revenue',
        type: 'api_usage',
        status: 'active',
        workerId: 'rw-scan-001',
        config: {
          targetRevenueUsd: 8000,
          maxCostUsd: 1000,
          minMarginPercent: 65,
          reinvestPercent: 20,
          channels: [
            { type: 'stripe', config: { productId: 'regwatch' }, enabled: true },
          ],
          riskLimits: { maxDailyLossUsd: 100, maxConcurrentWorkers: 3, killSwitchThreshold: -500 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'pl-agentguard-audit-001',
        name: 'AgentGuard Audit Trail Monetization',
        description: 'Agent audit logs → compliance evidence → enterprise sales',
        type: 'data_sales',
        status: 'testing',
        workerId: 'ag-monitor-001',
        config: {
          targetRevenueUsd: 12000,
          maxCostUsd: 1500,
          minMarginPercent: 60,
          reinvestPercent: 30,
          channels: [
            { type: 'stripe', config: { productId: 'agentguard' }, enabled: true },
            { type: 'api_marketplace', config: { platform: 'custom', pricing: 'per_agent' }, enabled: false },
          ],
          riskLimits: { maxDailyLossUsd: 150, maxConcurrentWorkers: 2, killSwitchThreshold: -750 },
        },
        metrics: { totalRevenueUsd: 0, totalCostUsd: 0, netProfitUsd: 0, roiPercent: 0, runsCount: 0, avgRevenuePerRun: 0 },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    loops.forEach(l => this.registerProfitLoop(l));
    return loops;
  }

  // Get system-wide profit metrics
  getSystemProfitMetrics(): { totalRevenue: number; totalCost: number; netProfit: number; roi: number; activeLoops: number; activeWorkers: number } {
    let totalRevenue = 0;
    let totalCost = 0;
    let activeLoops = 0;
    let activeWorkers = 0;

    this.profitLoops.forEach(loop => {
      if (loop.status === 'active') {
        activeLoops++;
        totalRevenue += loop.metrics.totalRevenueUsd;
        totalCost += loop.metrics.totalCostUsd;
      }
    });

    this.workers.forEach(worker => {
      if (worker.status !== 'error') activeWorkers++;
    });

    const netProfit = totalRevenue - totalCost;
    const roi = totalCost > 0 ? (netProfit / totalCost) * 100 : 0;

    return { totalRevenue, totalCost, netProfit, roi, activeLoops, activeWorkers };
  }
}

export const aiWorkerEngine = new AIWorkerEngine();